import React from "react";
import { FloatingNotePreview, HEADS_UP_PREVIEW_PLACEMENT } from "@/components/item-preview";
import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSTextColor,
  MDSTextOverflow,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import type { FC } from "react";
import { observer } from "mobx-react-lite";
import type { Maybe } from "@/domains/common/types";
import type { SmartNotePreviewInfo } from "./smart-note-preview.types";
import { useHeadsUpPreviewHover } from "./use-heads-up-preview-hover";
import {
  SmartNoteRowWrapper,
  NoteRow,
  LeadingIcon,
  NoteContent,
  NoteTitle,
  NoteSubtitle,
} from "./smart-note-row.styles";

export interface SmartNoteRowProps {
  noteId: string;
  primary: string;
  secondary?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  notePreviewInfo?: Maybe<SmartNotePreviewInfo>;
  onPreviewOpen?: () => void;
}

export const SmartNoteRow: FC<SmartNoteRowProps> = observer(
  ({ noteId: _noteId, primary, secondary, onClick, notePreviewInfo, onPreviewOpen }) => {
    const { hoverState, handleMouseEnter, handleMouseLeave } = useHeadsUpPreviewHover({
      noteId: _noteId,
      notePreviewInfo,
      onPreviewOpen,
    });

    return (
      <SmartNoteRowWrapper>
        <NoteRow onClick={onClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <LeadingIcon $isDoubleRow={!!secondary}>
            <MDSIcon kind={MDSIconKind.Document} color={MDSIconColor.Secondary} size={11} />
          </LeadingIcon>
          <NoteContent>
            <NoteTitle
              title={primary}
              variant={MDSTextVariant.TextS}
              weight={MDSTextWeight.Regular}
              overflow={MDSTextOverflow.Ellipsis}
              numberOfLines={1}
            >
              {primary}
            </NoteTitle>
            {secondary ? (
              <NoteSubtitle
                title={secondary}
                variant={MDSTextVariant.TextXs}
                color={MDSTextColor.Secondary}
                overflow={MDSTextOverflow.Ellipsis}
              >
                {secondary}
              </NoteSubtitle>
            ) : null}
          </NoteContent>
        </NoteRow>

        {hoverState.hovering && notePreviewInfo?.noteObservable && (
          <FloatingNotePreview
            state={hoverState}
            noteObservable={notePreviewInfo.noteObservable}
            placement={HEADS_UP_PREVIEW_PLACEMENT}
          />
        )}
      </SmartNoteRowWrapper>
    );
  }
);
